import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSupabase, isSupabaseConfigured } from '../../lib/supabase';
import { validateAccessCode, insertUserAccess, normalizeAccessCode } from '../../lib/portalAccess';
type AuthMode = 'login' | 'register' | 'forgot';
interface AuthCardProps {
  onAuthenticated: () => void;
}
export function AuthCard({
  onAuthenticated
}: AuthCardProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const supabase = getSupabase();
  // Debug: confirm env vars on mount (helps diagnose "Error de conexión")
  useEffect(() => {
    const env = (import.meta as any).env || {};
    console.log('[Portal] Supabase URL:', env.VITE_SUPABASE_URL || '(missing)');
    console.log('[Portal] Supabase Key exists:', !!env.VITE_SUPABASE_ANON_KEY);
    console.log('[Portal] Supabase configured:', isSupabaseConfigured);
  }, []);
  const mapAuthError = (err: any, action: AuthMode): string => {
    const raw = (err?.message || '').toString();
    const lower = raw.toLowerCase();
    if (lower.includes('invalid login') || lower.includes('invalid credentials')) {
      return 'Correo o contraseña incorrectos.';
    }
    if (lower.includes('email not confirmed')) {
      return 'Debes confirmar tu correo antes de iniciar sesión.';
    }
    if (lower.includes('already registered') || lower.includes('already exists')) {
      return 'Este correo ya está registrado. Inicia sesión.';
    }
    if (lower.includes('password') && lower.includes('6')) {
      return 'La contraseña debe tener al menos 6 caracteres.';
    }
    if (lower.includes('fetch') || lower.includes('network') || lower.includes('failed to fetch')) {
      return 'No pudimos conectar con el servidor. Revisa tu conexión.';
    }
    return raw || 'Ocurrió un error. Intenta nuevamente.';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    if (!supabase) {
      console.error('[Portal] Supabase client is null — missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY env vars.');
      setError('Supabase no está configurado. Revisa las variables de entorno (VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY).');
      setLoading(false);
      return;
    }
    try {
      if (mode === 'login') {
        const {
          data,
          error: signInError
        } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (signInError) {
          console.error('[Portal] Supabase login error:', signInError);
          throw signInError;
        }
        console.log('[Portal] Login success:', data.user?.email);
        onAuthenticated();
      } else if (mode === 'register') {
        if (!accessCode || accessCode.length < 4) {
          throw new Error('Código de acceso inválido.');
        }
        // ── 1. Validate access code against access_codes table ─────────
        const normalizedCode = normalizeAccessCode(accessCode);
        const codeRow = await validateAccessCode(normalizedCode);
        if (!codeRow) {
          throw new Error('Código de acceso inválido.');
        }
        console.log('[Portal] Access code valid:', {
          code: codeRow.code,
          realm: codeRow.realm,
          access_type: codeRow.access_type
        });
        // ── 2. Create the Supabase auth user ───────────────────────────
        const {
          data: signUpData,
          error: signUpError
        } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: fullName,
              full_name: fullName,
              access_code: normalizedCode,
              realm: codeRow.realm,
              access_type: codeRow.access_type
            }
          }
        });
        if (signUpError) {
          console.error('[Portal] Supabase signup error:', signUpError);
          throw signUpError;
        }
        console.log('[Portal] Signup success:', signUpData.user?.email);
        // ── 3. Insert into user_access table ───────────────────────────
        const userId = signUpData.user?.id;
        if (userId) {
          try {
            await insertUserAccess({
              userId,
              email,
              accessCode: normalizedCode,
              realm: codeRow.realm,
              accessType: codeRow.access_type
            });
          } catch (insertError: any) {
            console.error('[Portal] user_access insert failed (non-fatal):', insertError);
            // Don't block sign-up if user_access insert fails — metadata
            // already carries the access info. The portal can fall back
            // to user_metadata when reading access.
          }
        }
        setMessage('Cuenta creada exitosamente. Iniciando sesión...');
        setTimeout(() => {
          onAuthenticated();
        }, 1500);
      } else if (mode === 'forgot') {
        const {
          error: resetError
        } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/portal-de-usuario` : undefined
        });
        if (resetError) {
          console.error('[Portal] Supabase reset error:', resetError);
          throw resetError;
        }
        setMessage('Te hemos enviado un enlace para recuperar tu contraseña.');
      }
    } catch (err: any) {
      setError(mapAuthError(err, mode));
    } finally {
      setLoading(false);
    }
  };
  return <div className="w-full max-w-md mx-auto" data-id="element-494">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="relative rounded-2xl p-[1px] overflow-hidden" style={{
      background: 'linear-gradient(145deg, rgba(201,168,76,0.4) 0%, rgba(201,168,76,0.05) 100%)'
    }} data-id="element-495">
        <div className="relative bg-cosmic-black/95 backdrop-blur-xl rounded-2xl p-8 md:p-10" data-id="element-496">
          <div className="text-center mb-8" data-id="element-497">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2" data-id="element-498">
              {mode === 'login' && 'Iniciar Sesión'}
              {mode === 'register' && 'Activar tu Reino'}
              {mode === 'forgot' && 'Recuperar Acceso'}
            </h2>
            <p className="text-gold-light/80 font-serif italic text-sm" data-id="element-499">
              {mode === 'login' && 'Ingresa a tu biblioteca privada.'}
              {mode === 'register' && 'Crea tu cuenta para acceder a tus documentos.'}
              {mode === 'forgot' && 'Ingresa tu correo para restablecer tu contraseña.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-500">
            <AnimatePresence mode="wait" data-id="element-501">
              {mode === 'register' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="space-y-1" data-id="element-502">
                  <label className="block text-xs font-serif tracking-[0.1em] text-gold-light/70 uppercase" data-id="element-503">
                    Nombre completo
                  </label>
                  <input type="text" required value={fullName} onChange={e => setFullName(e.target.value)} className="w-full bg-white/5 border border-gold-primary/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-primary/50 transition-colors" placeholder="Tu nombre" data-id="element-504" />
                </motion.div>}
            </AnimatePresence>

            <div className="space-y-1" data-id="element-505">
              <label className="block text-xs font-serif tracking-[0.1em] text-gold-light/70 uppercase" data-id="element-506">
                Correo electrónico
              </label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/5 border border-gold-primary/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-primary/50 transition-colors" placeholder="correo@ejemplo.com" data-id="element-507" />
            </div>

            <AnimatePresence mode="wait" data-id="element-508">
              {mode !== 'forgot' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="space-y-1" data-id="element-509">
                  <label className="block text-xs font-serif tracking-[0.1em] text-gold-light/70 uppercase" data-id="element-510">
                    Contraseña
                  </label>
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white/5 border border-gold-primary/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-primary/50 transition-colors" placeholder="••••••••" data-id="element-511" />
                </motion.div>}
            </AnimatePresence>

            <AnimatePresence mode="wait" data-id="element-512">
              {mode === 'register' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="space-y-1" data-id="element-513">
                  <label className="block text-xs font-serif tracking-[0.1em] text-gold-light/70 uppercase" data-id="element-514">
                    Código de acceso
                  </label>
                  <input type="text" required value={accessCode} onChange={e => setAccessCode(e.target.value)} className="w-full bg-white/5 border border-gold-primary/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-primary/50 transition-colors" placeholder="Coloca el codigo que recibiste aquí" data-id="element-515" />
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-516">
                    Incluido en tu compra. Usa el código enviado a tu correo
                    para activar tu Reino.
                  </p>
                </motion.div>}
            </AnimatePresence>

            {error && <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/20 text-red-400 text-sm text-center" data-id="element-517">
                {error}
              </div>}

            {message && <div className="p-3 rounded-lg bg-green-900/20 border border-green-500/20 text-green-400 text-sm text-center" data-id="element-518">
                {message}
              </div>}

            <button type="submit" disabled={loading} className="w-full py-4 rounded-xl font-bold tracking-[0.15em] text-sm transition-all duration-300 disabled:opacity-50" style={{
            background: 'linear-gradient(135deg, #C9A84C 0%, #E8D48B 100%)',
            color: '#050508',
            boxShadow: '0 0 20px rgba(201,168,76,0.3)'
          }} data-id="element-519">
              {loading ? 'PROCESANDO...' : mode === 'login' ? 'INICIAR SESIÓN' : mode === 'register' ? 'CREAR CUENTA' : 'ENVIAR ENLACE'}
            </button>
          </form>

          <div className="mt-8 flex flex-col items-center gap-4 text-sm" data-id="element-520">
            {mode === 'login' && <>
                <button onClick={() => {
              setMode('forgot');
              setError(null);
              setMessage(null);
            }} className="text-gold-light/60 hover:text-gold-light transition-colors" data-id="element-521">
                  ¿Olvidaste tu contraseña?
                </button>
                <div className="h-px w-12 bg-white/10" data-id="element-522" />
                <button onClick={() => {
              setMode('register');
              setError(null);
              setMessage(null);
            }} className="text-white/60 hover:text-white transition-colors" data-id="element-523">
                  ¿No tienes cuenta?{' '}
                  <span className="text-gold-light" data-id="element-524">Regístrate</span>
                </button>
              </>}
            {mode === 'register' && <button onClick={() => {
            setMode('login');
            setError(null);
            setMessage(null);
          }} className="text-white/60 hover:text-white transition-colors" data-id="element-525">
                ¿Ya tienes cuenta?{' '}
                <span className="text-gold-light" data-id="element-526">Inicia sesión</span>
              </button>}
            {mode === 'forgot' && <button onClick={() => {
            setMode('login');
            setError(null);
            setMessage(null);
          }} className="text-white/60 hover:text-white transition-colors" data-id="element-527">
                Volver a <span className="text-gold-light" data-id="element-528">Iniciar sesión</span>
              </button>}
          </div>
        </div>
      </motion.div>
    </div>;
}